/* pamela-clipart-registry.js
   Manifest-based registry for pamela-clipart-library.js
   Loads Shopify-hosted JSON once and provides PAMELA_CLIPART_ENSURE().
*/
(function () {
  function slugify(s) {
    return String(s || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function isSizeLike(s) {
    return /^\d{2,4}\s*x\s*\d{2,4}$/i.test(String(s || "").trim());
  }

  // Remove UUID-ish tail from names so the UI shows clean labels again
  // Example: "Dog 12deb6ae-bc33-49b8-8391-ff06f44ff89a" => "Dog"
function cleanDisplayName(name) {
  let n = String(name || "").trim().toLowerCase();

  // remove file extension if present
  n = n.replace(/\.svg$/i, "");

  // remove trailing numbers (like 1234 or 1234a)
  n = n.replace(/\s*\d+[a-z]?\s*$/i, "");

  // normalize spaces
  n = n.replace(/\s+/g, " ").trim();

  // fallback if empty (like "2505.svg")
  if (!n) return "";

  // capitalize nicely
  n = n.replace(/\b\w/g, (c) => c.toUpperCase());

  return n;
}

  function normalizeSubcategory(category, subcategory) {
    const sub = String(subcategory || "").trim();
    return sub;
  }

  // Manual overrides you asked for (exact matches after cleaning)
  const CATEGORY_OVERRIDES = {
    // Animals
    "Farm Cow": "Animals",
    "Frog": "Animals",

    // Bugs & Insects
    "Ladybug": "Bugs & Insects",
    "Butterfly": "Bugs & Insects",
    "Dragonfly": "Bugs & Insects",

    // Flowers/Plants
    "Pinecones": "Flowers/Plants",
    "Daffodil": "Flowers/Plants",
    "Grapes": "Flowers/Plants",
    "Iris": "Flowers/Plants",
    "Ivy Vines": "Flowers/Plants",
    "Shamrocks": "Flowers/Plants",
  };

  // Special case: catch Pine Cones even if spelled differently
  function isPineConesLike(cleanedName) {
    const s = String(cleanedName || "").trim().toLowerCase();
    // pinecone, pinecones, pine cone, pine cones, pine-cones, pine-cone
    return /^pine[\s-]?cones?$/.test(s);
  }

  // Keyword-based fallback ONLY used when category is missing/junk (like 800x800)
  function inferCategoryFromName(name) {
    const n = String(name || "").toLowerCase();

    // Bugs & Insects
    if (/(ladybug|butterfly|dragonfly|bee|bees|wasp|wasps|ant|ants|spider|spiders|bug|bugs|insect|insects)/.test(n))
      return "Bugs & Insects";

    // Religious
    if (/(cross|crucifix|jesus|christ|bible|pray|prayer|angel|dove|church|ichthys|inri)/.test(n))
      return "Religious";

    // Wedding / Rings
    if (/(rings|wedding|marriage|couple|bride|groom|anniversary|hearts?)/.test(n))
      return "Rings";

    // Flowers/Plants
    if (/(rose|roses|lily|lilies|tulip|flower|flowers|daisy|sunflower|violet|orchid|ivy|vines?|shamrock|grapes?|pine\s*-?\s*cones?|pinecone|daffodil|iris)/.test(n))
      return "Flowers/Plants";

    // Military / patriotic
    if (/(flag|usa|american|veteran|army|navy|marine|air force|coast guard)/.test(n))
      return "Patriotic";

    // Sports / hobbies
    if (/(golf|fishing|hunt|hunting|baseball|basketball|football|soccer)/.test(n))
      return "Hobbies";

    // Animals (expanded)
    if (/(bear|deer|buck|elk|fish\b|bass|dog|cat|horse|cow|frog|eagle|bird|cardinal|dolphin|lion|wolf|duck|goose|rabbit|bunny|squirrel)/.test(n))
      return "Animals";

    return "Uncategorized";
  }

  function normalizeCategory(cat, cleanedName) {
    // 1) Manual overrides win
    if (CATEGORY_OVERRIDES[cleanedName]) return CATEGORY_OVERRIDES[cleanedName];

    // 1b) Pine Cones variants
    if (isPineConesLike(cleanedName)) return "Flowers/Plants";

    // 2) Use manifest category if it is valid (and not junk like 800x800)
    const c = String(cat || "").trim();
    if (c && !isSizeLike(c) && c.toLowerCase() !== "uncategorized") {
      // If the manifest says "Flowers", relabel to "Flowers/Plants" for consistency
      if (c.toLowerCase() === "flowers") return "Flowers/Plants";
      return c;
    }

    // 3) Infer from name
    return inferCategoryFromName(cleanedName);
  }

  // ~12k items / ~2.4MB -- was being re-downloaded and re-normalized from
  // scratch on every single library open (cache:"no-store", plus the server
  // sent no Cache-Control header for the browser HTTP cache to use either).
  // Persist the already-normalized result in localStorage so a repeat open
  // -- even after a full page reload -- is instant instead of a multi-second
  // wait. TTL matches the server's own Cache-Control max-age, so this is
  // never more than ~30 min behind Pamela's latest upload.
  const LS_KEY = "pamelaClipartManifestCacheV1";
  const LS_TTL_MS = 30 * 60 * 1000;

  // A hard/plain reload (F5, Ctrl+R, the browser reload button) is the one
  // signal a customer or Pamela has that means "get me current data" -- but
  // it only busts the browser's own HTTP cache, not this app-level
  // localStorage cache, so a reload could still show up-to-30-min-stale
  // clipart with no way to tell it's a cache problem (see Pamela's
  // 2026-08-26 report: cleared cookies + hard-refreshed and still saw a
  // stale library). Treat any reload as a cue to skip the cached read (a
  // fresh fetch is still written back for the next normal navigation).
  function isReloadNavigation() {
    try {
      const nav = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
      if (nav) return nav.type === "reload";
      return performance.navigation && performance.navigation.type === 1;
    } catch (_) {
      return false;
    }
  }

  function readManifestCache() {
    try {
      if (isReloadNavigation()) return null;
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.all) || !(Date.now() - Number(parsed.savedAt) < LS_TTL_MS)) return null;
      return parsed.all;
    } catch (_) {
      return null;
    }
  }

  function writeManifestCache(all) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ savedAt: Date.now(), all }));
    } catch (_) {
      // Quota exceeded / private browsing / disabled storage -- caching is
      // an optimization only, never let it block the actual library load.
    }
  }

  function publishManifest(all) {
    const byCat = {};
    for (const it of all) {
      const k = it.categoryId || "uncategorized";
      (byCat[k] ||= []).push(it);
    }

    window.PAMELA_CLIPART_ALL = all;
    window.PAMELA_CLIPART_BY_CAT = byCat;
    window.PAMELA_CLIPART = all;

    window.PAMELA_CLIPART_CATEGORIES = Object.keys(byCat)
      .sort((a, b) => a.localeCompare(b))
      .map((id) => {
        const first = byCat[id] && byCat[id][0] ? byCat[id][0].category : id;
        return { id, label: first };
      });

    return all;
  }

  async function loadManifestOnce() {
    // Already loaded?
    if (Array.isArray(window.PAMELA_CLIPART_ALL) && window.PAMELA_CLIPART_ALL.length) {
      return window.PAMELA_CLIPART_ALL;
    }
    // In-flight?
    if (window.__PAMELA_CLIPART_MANIFEST_PROMISE) {
      return window.__PAMELA_CLIPART_MANIFEST_PROMISE;
    }

    const cached = readManifestCache();
    if (cached) {
      return publishManifest(cached);
    }

    // Public, no key needed -- this is a read-only catalog of already-public
    // clipart files. It used to require the master admin key, which meant
    // embedding that same key (also guarding customer PII and destructive
    // admin actions) in this public page's JS. Don't put it back.
    const url = "https://cm-editor-server.onrender.com/api/svg-library";
    if (!url) throw new Error("PAMELA_CLIPART_MANIFEST_URL is missing on window.");

    window.__PAMELA_CLIPART_MANIFEST_PROMISE = fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error("Manifest fetch failed: " + r.status + " " + r.statusText);
        return r.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("Manifest JSON is not an array.");

        const all = data
          .map((it) => {
            const rawName = String(it.name || "");
            const cleanedName = cleanDisplayName(rawName);
            const category = normalizeCategory(it.category, cleanedName);
            const subcategory = normalizeSubcategory(category, it.subcategory);

        return {
          id: String(it.id || ""),
          name: cleanedName,          // clean label shown in UI
          rawName: rawName,           // keep original around for future order metadata
          category,
          categoryId: slugify(category || "Uncategorized"),
          subcategory,
          url: "https://cm-editor-server.onrender.com" + String(it.url || ""),
          // Small pre-rendered preview for the picker grid ONLY -- empty
          // string for anything not processed yet, so the picker's
          // `it.thumbUrl || it.url` falls back to the original file exactly
          // like before. Never used for placement/production size.
          thumbUrl: it.thumbUrl ? "https://cm-editor-server.onrender.com" + String(it.thumbUrl) : "",
          // Real-world width/height in inches from the SVG (server reads
          // width/height="NN.NNin"). Drive the too-big-for-stone grey-out filter
          // (clipart never shrinks, so too WIDE or too TALL both block); null
          // when unreadable/missing.
          widthIn: (Number.isFinite(it.widthIn) && it.widthIn > 0) ? it.widthIn : null,
          heightIn: (Number.isFinite(it.heightIn) && it.heightIn > 0) ? it.heightIn : null,
          // Keywords pulled from the original CDR files' Tags field (server
          // matches them in by the numeric ID in the filename). Lets search
          // find e.g. "cowboy" even when the item is only filed under Western.
          keywords: Array.isArray(it.keywords) ? it.keywords : [],
        };
          })
          .filter((it) => it.url);

        writeManifestCache(all);
        return publishManifest(all);
      })
      .catch((e) => {
        console.error("[Pamela Clipart Registry] Manifest load error:", e);
        throw e;
      });

    return window.__PAMELA_CLIPART_MANIFEST_PROMISE;
  }

  // The library calls: await window.PAMELA_CLIPART_ENSURE(activeCat)
  // =============================
// Subcategory helper
// =============================
window.PAMELA_GET_SUBCATEGORIES = function (catId) {
  const cid = slugify(catId || "") || "uncategorized";
  const items = window.PAMELA_CLIPART_BY_CAT?.[cid] || [];

  const map = {};

  for (const it of items) {
    const sub = String(it.subcategory || "").trim();
    if (!sub) continue;

    const key = sub.toLowerCase();
    if (!map[key]) {
      map[key] = {
        id: key,
        label: sub
      };
    }
  }

  return Object.values(map).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

  window.PAMELA_CLIPART_ENSURE = async function (catId) {
    const all = await loadManifestOnce();

    const cid = slugify(catId || "") || "uncategorized";
    window.PAMELA_CLIPART_BY_CAT ||= {};
    window.PAMELA_CLIPART_BY_CAT[cid] ||= all.filter((it) => it.categoryId === cid);

    return true;
  };

  // Customers typically finish name/date text before ever opening the
  // clipart library, which previously only started loading the moment they
  // clicked "View Library" -- so the first click was always a cold wait.
  // Warm it in the background once the browser is idle (or after a short
  // fallback delay on browsers without requestIdleCallback), so by the time
  // they actually open the library it's usually already loaded.
  (function prefetchManifestInBackground() {
    const kick = () => { loadManifestOnce().catch(() => {}); };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(kick, { timeout: 4000 });
    } else {
      setTimeout(kick, 1500);
    }
  })();
})();
